import fetch from './Fetcher';
import { request } from './request';
import { AppCache } from './AppCache';
import tokenManager from './tokenManager';

/* --- new helper: merges Authorization header when a JWT is present ---------- */
const withAuth = (options = {}) => {
  const token = tokenManager.getToken();
  if (!token) return options;

  return {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`
    }
  };
};

export const api = {
    get: endpoint => fetch(`/api${endpoint}`, withAuth(request.get)),
    delete: endpoint => fetch(`/api${endpoint}`, withAuth(request.delete)),
    update: (endpoint, data) => {
        return fetch(
            `/api${endpoint}`, {
                ...withAuth(request.put),
                body: JSON.stringify(data)
            }
        )
    },
    create: (endpoint, data) => {
        return fetch(
            `/api${endpoint}`, {
                ...withAuth(request.post),
                body: JSON.stringify(data)
            }
        )
    },
    createExternal: (endpoint, data, options = {}) => {
        return fetch(
            endpoint, {
                ...request.post,
                ...options,
                headers: {
                    ...request.post.headers,
                    ...(options.headers || {})
                },
                body: JSON.stringify(data)
            }
        )
    },
    postFormData: (endpoint, data) => {
        return fetch(
            `/api${endpoint}`, {
                ...withAuth(request.postFormData),
                body: data
            }
        )
    },
    putFormData: (endpoint, data) => {
        return fetch(
            `/api${endpoint}`, {
                ...withAuth(request.putFormData),
                body: data
            }
        )
    },
    createInterval: (callback, timeoutMilliseconds, ) => {
        const intervalObject = {
            started: false, // will contain the id of the started interval
            start: (param) => {
                const createdInterval = setInterval(() => {
                    callback(param)
                }, [timeoutMilliseconds])

                intervalObject.started = createdInterval
            },
            clear: () => {
                clearInterval(intervalObject.started)
                intervalObject.started = false
            }
        }

        return intervalObject
    }
}

export function createCachedService(cacheName) {
    return {
        get: async endpoint => {
            const cache = AppCache.getCache(cacheName)
            if (cache[endpoint]) {
                return cache[endpoint]
            } else {
                const result = await fetch(`/api${endpoint}`, request.get)
                cache[endpoint] = result

                return result
            }
        },
        delete: api.delete,
        update: api.update,
        create: api.create
    }
}

/**
 * Creates a new FormData and adds each key value from the json object to it.
 * @param {*} jsonObject 
 * @returns 
 */
export function buildFormData (jsonObject) {
    const formData = new FormData()
    for (const key in jsonObject) {
        if (Object.hasOwnProperty.call(jsonObject, key) && ![null, undefined].includes(jsonObject[key])) {
            formData.append(key, jsonObject[key])
        }
    }

    return formData
}

/**
 * { key1: 'value1', key2: null, key3: '', key4: true, key5: 5, key6: false } would result into
 * key1=value1&key4=true&key5=5&key6=false
 * @param {*} jsonObject
 * @returns 
 */
export function buildQueryString (jsonObject) {
    const nonEmptyParamEntries =Array
        .from(Object.entries(jsonObject))
        .filter(([key, value]) => ![null, undefined, ''].includes(value))
    
    const queryString = (new URLSearchParams(Object.fromEntries(nonEmptyParamEntries))).toString()

    return queryString
}

/**
 * Parses the location string or a provided query string to nested json object.
 * For nested objects. E.g. say the location string is order_by=report_order&page=1&query=project_name%3Db%26name%3Da%26project_category%3Drnd%26business_unit%3Db
 * This is a nested query case where the query param gets parsed to "project_name=b&name=a&project_category=rnd&business_unit=b"
 * In this case, because there is '=' in the value of 'query', then that value will be parsed again - to turn it into a json.
 * Ther first run of the function will result in
 * {
 *  "order_by": "report_order",
 *  "page": "1",
 *  "query": "project_name=b&name=a&project_category=rnd&business_unit=b"
 * }
 * 
 * Because of the recursion, the final result will be
 * {
 *      "order_by": "report_order",
 *      "page": "1",
 *      "query": {
 *          "project_name": "b",
 *          "name": "a",
 *          "project_category": "rnd",
 *          "business_unit": "b"
 *      }
 * }
 * 
 * @param {*} queryString 
 * @returns 
 */
export function parseQueryString (queryString = null) {
    const queryParams = new URLSearchParams(queryString || window.location.search)
    const jsonQueryParams = {}
    // entries are key value parirs - e.g. project_name='test' => ['project_name', 'test']
    for(const [key, value] of queryParams.entries()) {
        jsonQueryParams[key] = value
    }

    for (const key in jsonQueryParams) {
        if (jsonQueryParams[key].includes('=')) {
            jsonQueryParams[key] = parseQueryString(jsonQueryParams[key])
        }
    }

    return jsonQueryParams
}

/**
 * 
 * @param {Array} routes   // an array of models - e.g. if it is a companies service then simply pass ['companies'].
 *                         // However if it is a nested service - e.g. companies/{id}/claim_groups - then pass ['companies', 'claim_groups'] instead.
 * @param {Object} methodSettings // An object specifying the methods of the service. Made to replicate the callbacks setup on the backend.
 *                         // { only: Array } - only these methods will be defined. E.g. { only: ['get', 'create' ] } will create a service with the get and create methods.
 *                         // { except: Array } - define all methods except the listed. E.g. { except: ['create'] }
 * @param {Object} formMethodSettings // Same as methodSettings except that almost all services do not need form methods so by default they are in the except array. E.g. { except: ['postFormData', 'putFormData'] }
 * @returns 
 */
export function buildService (modelsRoute, methodSettings, formMethodSettings = { except: ['postFormData', 'putFormData']}) {
    const buildInstanceSpecificUrl = (method, idParams) => {
        const routeToUse = ['get', 'update', 'delete', 'putFormData'].includes(method) ? modelsRoute + '/{id}' : modelsRoute

        let matchIndex = -1
        const instanceSpecificUrl = routeToUse.replace(/{id}/g, (match) => {
            matchIndex += 1
            const replacement = idParams[matchIndex] !== undefined ? idParams[matchIndex] : ''
            return replacement
        })

        if (instanceSpecificUrl.includes('//')) {
            const urlWithError = instanceSpecificUrl.replace('//', '/{missing param}/')
            console.error('Invalid request endpoint format. ', urlWithError, idParams)
            throw new Error('Invalid request endpoint format.')
        }

        return [instanceSpecificUrl, matchIndex]
    }

    const serviceObject = {
        base: { ...api },
        get: (...params) => {
            const [instanceSpecificUrl, lastMatchIndex] = buildInstanceSpecificUrl('get', params)

            const queryParamsObject = params[lastMatchIndex + 1]
            const queryString = queryParamsObject ? `?${buildQueryString(queryParamsObject) || ''}` : ''

            return api.get(`${instanceSpecificUrl}${queryString}`)
        },
        all: (...params) => {
            const [instanceSpecificUrl, lastMatchIndex] = buildInstanceSpecificUrl('all', params)

            // queryParamsObject is optional
            const queryParamsObject = params[lastMatchIndex + 1]
            const queryString = queryParamsObject ? `?${buildQueryString(queryParamsObject) || ''}` : ''

            return api.get(`${instanceSpecificUrl}${queryString}`)
        },
        update: (...params) => {
            const [instanceSpecificUrl, lastMatchIndex] = buildInstanceSpecificUrl('update', params)
            // queryParamsObject is optional

            let payload, queryParamsObject, queryString
            // if (lastMatchIndex > 0) {
            //     [payload, queryParamsObject] = [params[lastMatchIndex + 1], params[lastMatchIndex + 2]]
            //     queryString = queryParamsObject ? `?${buildQueryString(queryParamsObject) || ''}` : ''
            // } else {
            //     payload = params[0] === '' ? params[1] : params[0]
            //     queryString = ''
            // }

            [payload, queryParamsObject] = [params[lastMatchIndex + 1], params[lastMatchIndex + 2]]
            queryString = queryParamsObject ? `?${buildQueryString(queryParamsObject) || ''}` : ''

            return api.update(`${instanceSpecificUrl}${queryString}`, payload)
        },
        delete: (...params) => {
            const [instanceSpecificUrl, lastMatchIndex] = buildInstanceSpecificUrl('delete', params)

            // queryParamsObject is optional
            const queryParamsObject = params[lastMatchIndex + 1]
            const queryString = queryParamsObject ? `?${buildQueryString(queryParamsObject) || ''}` : ''

            return api.delete(`${instanceSpecificUrl}${queryString}`)
        },
        create: (...params) => {
            const [instanceSpecificUrl, lastMatchIndex] = buildInstanceSpecificUrl('create', params)

            // queryParamsObject is optional
            const [payload, queryParamsObject] = [params[lastMatchIndex + 1], params[lastMatchIndex + 2]]
            const queryString = queryParamsObject ? `?${buildQueryString(queryParamsObject) || ''}` : ''

            return api.create(`${instanceSpecificUrl}${queryString}`, payload)
        },
        postFormData: (...params) => {
            const [instanceSpecificUrl, lastMatchIndex] = buildInstanceSpecificUrl('postFormData', params)

            // queryParamsObject is optional
            const [payload, queryParamsObject] = [params[lastMatchIndex + 1], params[lastMatchIndex + 2]]
            const queryString = queryParamsObject ? `?${buildQueryString(queryParamsObject) || ''}` : ''

            return api.postFormData(`${instanceSpecificUrl}${queryString}`, payload)
        },
        putFormData: (...params) => {
            const [instanceSpecificUrl, lastMatchIndex] = buildInstanceSpecificUrl('putFormData', params)

            // queryParamsObject is optional
            const [payload, queryParamsObject] = [params[lastMatchIndex + 1], params[lastMatchIndex + 2]]
            const queryString = queryParamsObject ? `?${buildQueryString(queryParamsObject) || ''}` : ''

            return api.putFormData(`${instanceSpecificUrl}${queryString}`, payload)
        },
    }

    if ((methodSettings?.only && methodSettings?.except) || (formMethodSettings?.only && formMethodSettings?.except)) {
        throw new Error('Cannot pass both only and except options. Pick only one.')
    }

    const except = [...methodSettings?.except || [], ...formMethodSettings?.except || []]
    except.forEach(methodName => delete serviceObject[methodName])

    if (methodSettings?.only?.length) {
        Object.keys(serviceObject).forEach(methodName => {
            // delete all non required methods that are NOT form methods
            if (!methodSettings.only.includes(methodName) && !methodName.toLowerCase().includes('form')) {
                delete serviceObject[methodName]
            }
        })
    }

    if (formMethodSettings?.only?.length) {
        Object.keys(serviceObject).forEach(methodName => {
            // delete all non required methods that ARE form methods
            if (!formMethodSettings.only.includes(methodName) && methodName.toLowerCase().includes('form')) {
                delete serviceObject[methodName]
            }
        })
    }

    return serviceObject
}
